import '@resources/css/app.css';
import '@resources/js/vue-devtools.ts';
import axios from 'axios';
import * as luxon from 'luxon';
import { createLocker } from '@web/utils/Locker.ts';
import Bugsnag from '@bugsnag/js';
import ThirdPartyServices from '@web/utils/ThirdPartyServices.ts';
import { reactive } from 'vue';
import installToastVuePlugin from '@web/vuePluginInstallers/installToastVuePlugin.ts';
import installBugsnagVuePlugin from '@web/vuePluginInstallers/installBugsnagVuePlugin.ts';
import eventBus from '@web/utils/eventBus';
import { handleIncomingAssetsVersionFromAxiosResponse } from '@web/utils/assetsVersion.ts';
import startBugsnag from '@web/utils/startBugsnag.ts';
import blurNumberInputOnWheel from '@web/utils/blurNumberInputOnWheel.ts';

import FormControl from '@web/directives/FormControl.js';
import FocusableError from '@web/directives/FocusableError.ts';
import ClickOutside from '@web/directives/ClickOutside.js';
import AutoSize from '@web/directives/AutoSize.js';
import Resize from '@web/directives/Resize.js';
import Intersect from '@web/directives/Intersect.js';
import Tooltip from '@web/directives/Tooltip.ts';
import Focus from '@web/directives/Focus.js';
import PreventAutocomplete from '@web/directives/PreventAutocomplete.js';
import {
  formatInteger,
  formatMoney,
  formatMoneyWithCents,
  formatPerCreditRate,
  formatPercent,
} from '@web/utils/numberFormatters';
import formatDuration from '@web/utils/formatDuration.ts';
import pluralize from '@web/utils/pluralize.ts';
import timeago from '@web/utils/timeago.ts';
import truncate from '@web/utils/truncate.js';
import formatPhoneNumber from '@web/utils/formatPhoneNumber.ts';
import formatDateTime from '@web/utils/formatDateTime.ts';
import { createPinia } from 'pinia';
import { DataAttributeMaker } from '@clearstream/data-attributes';

// This polyfill solves an issue with TipTap using the `findLast` method
// that is not supported in some browsers, like Safari 14 and some releases of Safari 15.
// See: https://getclearstream.slack.com/archives/C0218EBRYBC/p1756304730008489?thread_ts=1756304602.652139&cid=C0218EBRYBC
import 'core-js/actual/array/find-last';
import 'core-js/actual/object/has-own';
import { random } from 'es-toolkit';

window.axios = axios;
window.luxon = luxon; // todo: remove
window.locker = createLocker();
window.Bus = eventBus;

// Locker's expired entries must be flushed automatically because
// localStorage (that is used under the hood) is limited to about 5MB.
// However, we don't want to do that on every page load, that's why
// we use the "lottery".
if (random(1, 100) <= 10) {
  locker.flushExpired();
}

// Hide contactKey data attribute until supported by the app.
delete DataAttributeMaker.REGISTERED_DATA_ATTRIBUTES['contactKey'];

window.axios.interceptors.response.use(
  (response) => {
    handleIncomingAssetsVersionFromAxiosResponse(response);

    return response;
  },
  (error) => {
    // If the `response` property does not exist on the `error` object,
    // it means we have faced a "Network error".
    // In that case, we won't do anything in the interceptor, and
    // will just let the component handle displaying an error,
    // if necessary, depending on the circumstance.
    // Reference: https://github.com/axios/axios/issues/383#issuecomment-316501886
    if (error.response) {
      handleIncomingAssetsVersionFromAxiosResponse(error.response);

      const statusCode = error.response.status;
      const errorType = error.response?.data?.error?.type;

      if (statusCode === 401) {
        if (['invalid_token'].includes(errorType)) {
          window.Bus.$emit('invalidToken');
        } else {
          window.Bus.$emit('sessionExpired');
        }
      }

      if (statusCode === 403) {
        if (['invalid_account', 'invalid_account_status'].includes(errorType)) {
          window.Bus.$emit('invalidAccount');
        }

        if (['invalid_user'].includes(errorType)) {
          window.Bus.$emit('invalidUser');
        }
      }
    }

    return Promise.reject(error);
  },
);

luxon.Settings.throwOnInvalid = true;

// Axios
window.axios.defaults.headers.common = {
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

// Bugsnag
startBugsnag({
  user: window.Clearstream.user
    ? {
        id: String(window.Clearstream.user.id),
        email: window.Clearstream.user.email,
        name: window.Clearstream.user.name,
      }
    : null,
  metadata: {
    account: window.Clearstream.account
      ? {
          id: String(window.Clearstream.account.id),
          name: window.Clearstream.account.name,
          role: window.Clearstream.account.user.role,
        }
      : null,
  },
});

window.bugsnagClient = Bugsnag;

ThirdPartyServices.init();

blurNumberInputOnWheel();

// Luxon
if (window.Clearstream?.user?.timezone) {
  luxon.Settings.defaultZone = window.Clearstream.user.timezone;
}

const ticker = reactive({ now: new Date() });
setInterval(() => {
  ticker.now = new Date();
}, 5000);

function bootVueApp(vueApp) {
  installBugsnagVuePlugin(vueApp);
  installToastVuePlugin(vueApp);
  vueApp.use(createPinia());

  vueApp.directive('form-control', FormControl);
  vueApp.directive('focusable-error', FocusableError);
  vueApp.directive('click-outside', ClickOutside);
  vueApp.directive('autosize', AutoSize);
  vueApp.directive('resize', Resize);
  vueApp.directive('intersect', Intersect);
  vueApp.directive('tooltip', Tooltip);
  vueApp.directive('focus', Focus);
  vueApp.directive('prevent-autocomplete', PreventAutocomplete);

  vueApp.config.globalProperties.$luxon = luxon;
  vueApp.config.globalProperties.$formatInteger = formatInteger;
  vueApp.config.globalProperties.$formatMoney = formatMoney;
  vueApp.config.globalProperties.$formatMoneyWithCents = formatMoneyWithCents;
  vueApp.config.globalProperties.$formatPerCreditRate = formatPerCreditRate;
  vueApp.config.globalProperties.$formatPercent = formatPercent;
  vueApp.config.globalProperties.$formatDuration = formatDuration;
  vueApp.config.globalProperties.$pluralize = pluralize;
  vueApp.config.globalProperties.$timeago = timeago;
  vueApp.config.globalProperties.$truncate = truncate;
  vueApp.config.globalProperties.$formatPhoneNumber = formatPhoneNumber;
  vueApp.config.globalProperties.$formatDateTime = formatDateTime;
  vueApp.config.globalProperties.$ticker = ticker;
  vueApp.config.globalProperties.$bus = eventBus;
  vueApp.config.globalProperties.window = window;
}

export { bootVueApp };
